import dayjs from 'dayjs';
import apis from '../../utils/apis';
import { AppThunk } from '../types';
import { CalendarEvent, CalendarFilters } from '../../components/Calendar/types';

/* ─── Fetch events for a date range ─── */
export const fetchCalendarEvents = (
  startDate: Date,
  endDate: Date,
  filters?: CalendarFilters
): AppThunk =>
  async (dispatch) => {
    dispatch({ type: 'FETCH_CALENDAR_EVENTS_LOADING' as const });
    try {
      const response = await apis({
        method: 'GET',
        url: '/calendar/events',
        params: {
          start_date: dayjs(startDate).toISOString(),
          end_date: dayjs(endDate).endOf('day').toISOString(),
          event_type: filters?.event_type,
          source_type: filters?.source_type,
          status: filters?.status,
        },
      });

      if (response?.status === 200) {
        dispatch({ type: 'FETCH_CALENDAR_EVENTS' as const, payload: response.data.events ?? [] });
      } else {
        dispatch({ type: 'FETCH_CALENDAR_EVENTS' as const, payload: [] });
      }
    } catch (error: any) {
      dispatch({ type: 'FETCH_CALENDAR_EVENTS_ERROR' as const, payload: error?.message ?? 'Failed to fetch events' });
    }
  };

/* ─── Get single event ─── */
export const fetchCalendarEvent = (uuid: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await apis({ method: 'GET', url: `/calendar/events/${uuid}` });
      if (response?.status === 200) {
        dispatch({ type: 'SET_CALENDAR_EVENT' as const, payload: response.data.event });
      }
    } catch {
      // silent
    }
  };

/* ─── Create event ─── */
export const createCalendarEvent = (event: Partial<CalendarEvent>): AppThunk =>
  async (dispatch) => {
    try {
      const response = await apis({ method: 'POST', url: '/calendar/events', data: event });
      if (response?.status === 201) {
        dispatch({ type: 'CREATE_CALENDAR_EVENT' as const, payload: response.data.event });
      }
    } catch {
      // silent
    }
  };

/* ─── Update event (bidirectional sync handled by backend) ─── */
export const updateCalendarEvent = (uuid: string, updates: Partial<CalendarEvent>): AppThunk =>
  async (dispatch) => {
    try {
      const response = await apis({ method: 'PUT', url: `/calendar/events/${uuid}`, data: updates });
      if (response?.status === 200) {
        dispatch({ type: 'UPDATE_CALENDAR_EVENT' as const, payload: response.data.event });
      }
    } catch {
      // silent
    }
  };

/* ─── Delete event ─── */
export const deleteCalendarEvent = (uuid: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await apis({ method: 'DELETE', url: `/calendar/events/${uuid}` });
      if (response?.status === 200) {
        dispatch({ type: 'DELETE_CALENDAR_EVENT' as const, payload: uuid });
      }
    } catch {
      // silent
    }
  };

/* ─── Trigger bulk sync (backfill from tasks / matters) ─── */
export const syncCalendarEvents = (): AppThunk =>
  async (dispatch) => {
    dispatch({ type: 'SET_CALENDAR_SYNC_STATUS' as const, payload: 'syncing' });
    try {
      const response = await apis({ method: 'POST', url: '/calendar/events/sync' });
      if (response?.status === 200) {
        dispatch({ type: 'SET_CALENDAR_SYNC_STATUS' as const, payload: 'success' });
      }
    } catch {
      dispatch({ type: 'SET_CALENDAR_SYNC_STATUS' as const, payload: 'error' });
    }
  };

/* ─── Export as .ics download ─── */
export const exportCalendarICS = async (startDate: Date, endDate: Date): Promise<Blob | null> => {
  try {
    const response = await apis({
      method: 'GET',
      url: '/calendar/export/ics',
      params: {
        start_date: dayjs(startDate).toISOString(),
        end_date: dayjs(endDate).toISOString(),
      },
      responseType: 'blob',
    });
    return response?.data ?? null;
  } catch {
    return null;
  }
};

/* ─── Import .ics file ─── */
export const importCalendarICS = (file: File): AppThunk =>
  async (dispatch) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apis({ method: 'POST', url: '/calendar/import/ics', data: formData });
      if (response?.status === 200) {
        // Re-fetch events to reflect imports
        dispatch({ type: 'SET_CALENDAR_SYNC_STATUS' as const, payload: 'success' });
      }
    } catch {
      dispatch({ type: 'SET_CALENDAR_SYNC_STATUS' as const, payload: 'error' });
    }
  };

/* ─── Feed token management ─── */
export const createFeedToken = (label?: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await apis({ method: 'POST', url: '/calendar/feed/token', data: { label } });
      if (response?.status === 201) {
        dispatch({ type: 'SET_CALENDAR_FEED_TOKEN' as const, payload: response.data.feed_url });
      }
    } catch {
      // silent
    }
  };

/* ─── Set local filters (no API call) ─── */
export const setCalendarFilters = (filters: Partial<CalendarFilters>): AppThunk =>
  (dispatch) => {
    dispatch({ type: 'SET_CALENDAR_FILTERS' as const, payload: filters });
  };
